// src/sim/map/zones.ts — pure polygon queries (MAP-02..11).
//
// Invariants:
//   - No side effects; polygons treated as read-only input.
//   - Point-in-polygon via ray casting (standard algorithm; no external lib).
//   - Centroid via vertex average (good enough for convex-ish zones; exact
//     polygon centroid overkill at this scale).
//
// Source: Phase 1 PATTERNS.md §src/sim/map/zones.ts.

import type { Vec2, ZoneId, ZonePolygon } from '../../shared/types/map';

/** Point-in-polygon via horizontal ray casting. Returns the first matching zone id or null. */
export function zoneAt(point: Vec2, zones: Map<ZoneId, ZonePolygon>): ZoneId | null {
  for (const [id, zone] of zones) {
    if (pointInPolygon(point, zone.vertices)) {
      return id;
    }
  }
  return null;
}

/** Centroid approximation: arithmetic mean of vertices. */
export function zoneCenter(zone: ZonePolygon): Vec2 {
  if (zone.vertices.length === 0) return { x: 0, y: 0 };
  let sx = 0;
  let sy = 0;
  for (const v of zone.vertices) {
    sx += v.x;
    sy += v.y;
  }
  return { x: sx / zone.vertices.length, y: sy / zone.vertices.length };
}

/** Standard ray-casting point-in-polygon. O(n) per query. */
function pointInPolygon(point: Vec2, vertices: Vec2[]): boolean {
  let inside = false;
  for (let i = 0, j = vertices.length - 1; i < vertices.length; j = i, i++) {
    const vi = vertices[i]!;
    const vj = vertices[j]!;
    const intersect =
      vi.y > point.y !== vj.y > point.y &&
      point.x < ((vj.x - vi.x) * (point.y - vi.y)) / (vj.y - vi.y) + vi.x;
    if (intersect) inside = !inside;
  }
  return inside;
}
